import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, X } from "lucide-react";
import { sfx } from "@/audio";
import { DURATION, GONE, MAX_TAPS, MULT_MAX, SPOTS, STEP, WAITING, advance, mult, newGame, tap as tapAt } from "@/city3d/rush";

const GOLD = "#F5B83D", GREEN = "#34D399", RED = "#F87171";
const HULLS = ["#F97316", "#E11D48", "#38BDF8", "#A78BFA", "#FB7185"];
const COLS = 3, ROWS = SPOTS / COLS, TOP = 110;
const POP = 160, AWAY = 380, FX = 650, SETTLE = 1000;

function text(c, s, x, y, px, color, align = "center") {
  c.font = `700 ${px}px "Space Grotesk", system-ui, sans-serif`;
  c.fillStyle = color;
  c.textAlign = align;
  c.fillText(s, x, y);
}

// The 3x3 harbour under the score, as big as fits.
function grid(W, H) {
  const room = H - TOP - 24, cell = Math.min(W / COLS, room / ROWS, 230);
  const x0 = (W - cell * COLS) / 2, y0 = TOP + (room - cell * ROWS) / 2;
  return { cell, x0, y0, at: (s) => [x0 + ((s % COLS) + 0.5) * cell, y0 + (Math.floor(s / COLS) + 0.5) * cell] };
}

// A ship seen from above, bow towards dir (1 right, -1 left).
function ship(c, x, y, len, color, dir, gold) {
  const w = len * 0.34;
  c.save();
  c.translate(x, y);
  c.scale(dir, 1);
  c.fillStyle = "rgba(255,255,255,.16)";
  c.beginPath();
  c.ellipse(-len * 0.55, 0, len * 0.3, w * 0.4, 0, 0, Math.PI * 2);
  c.fill();
  if (gold) {
    c.shadowColor = GOLD;
    c.shadowBlur = 22;
  }
  c.fillStyle = color;
  c.beginPath();
  c.moveTo(len / 2, 0);
  c.quadraticCurveTo(len * 0.25, -w / 2, -len * 0.1, -w / 2);
  c.lineTo(-len / 2, -w * 0.42);
  c.lineTo(-len / 2, w * 0.42);
  c.lineTo(-len * 0.1, w / 2);
  c.quadraticCurveTo(len * 0.25, w / 2, len / 2, 0);
  c.fill();
  c.shadowBlur = 0;
  c.strokeStyle = "rgba(0,0,0,.35)";
  c.lineWidth = 2;
  c.stroke();
  c.fillStyle = "#F8FAFC";
  c.fillRect(-len * 0.4, -w * 0.28, len * 0.2, w * 0.56);
  c.fillStyle = "rgba(15,23,42,.3)";
  for (let k = 0; k < 3; k++) c.fillRect(-len * 0.12 + k * len * 0.13, -w * 0.26, len * 0.1, w * 0.52);
  c.restore();
}

function paint(c, W, H, now, r, calm) {
  const { g, t } = r, L = grid(W, H), live = r.mode === "run" || r.mode === "done";
  const gr = c.createLinearGradient(0, 0, 0, H);
  gr.addColorStop(0, "#0B1F3A");
  gr.addColorStop(1, "#14427A");
  c.fillStyle = gr;
  c.fillRect(0, 0, W, H);
  c.strokeStyle = "rgba(255,255,255,.05)";
  c.lineWidth = 2;
  for (let y = 40; y < H; y += 46) {
    c.beginPath();
    for (let x = 0; x <= W; x += 24) c.lineTo(x, y + (calm ? 0 : Math.sin(x / 60 + now / 700 + y) * 4));
    c.stroke();
  }
  c.setLineDash([6, 8]);
  c.strokeStyle = "rgba(255,255,255,.12)";
  for (let s = 0; s < SPOTS; s++) {
    const [x, y] = L.at(s);
    c.beginPath();
    c.arc(x, y, L.cell * 0.4, 0, Math.PI * 2);
    c.stroke();
  }
  c.setLineDash([]);

  if (live) {
    g.ships.forEach((sh, i) => {
      const [x, y] = L.at(sh.spot), end = sh.at + sh.life, len = L.cell * (sh.gold ? 0.5 : 0.66), dir = i % 2 ? -1 : 1;
      const color = sh.gold ? GOLD : HULLS[i % HULLS.length];
      if (t < sh.at) return;
      if (g.state[i] === WAITING) {
        const k = Math.min(1, (t - sh.at) / POP), left = (end - t) / sh.life;
        c.save();
        c.translate(x, y);
        c.scale(0.5 + 0.5 * k, 0.5 + 0.5 * k);
        ship(c, 0, 0, len, color, dir, sh.gold);
        c.restore();
        c.strokeStyle = left > 0.3 ? "rgba(255,255,255,.75)" : RED;
        c.lineWidth = 4;
        c.beginPath();
        c.arc(x, y, L.cell * 0.4, -Math.PI / 2, -Math.PI / 2 + left * Math.PI * 2);
        c.stroke();
      } else if (g.state[i] === GONE && t < end + AWAY) {
        const k = (t - end) / AWAY;
        c.globalAlpha = 1 - k;
        ship(c, x + dir * k * L.cell * 0.6, y, len, color, dir, sh.gold);
        c.globalAlpha = 1;
      }
    });
  }
  r.fx = r.fx.filter((f) => now - f.t0 < FX);
  for (const f of r.fx) {
    const k = (now - f.t0) / FX, [x, y] = L.at(f.s), sh = f.i != null && g.ships[f.i];
    c.globalAlpha = 1 - k;
    if (sh) {
      c.save();
      c.translate(x, y);
      c.scale(1 - k, 1 - k);
      ship(c, 0, 0, L.cell * (sh.gold ? 0.5 : 0.66), sh.gold ? GOLD : HULLS[f.i % HULLS.length], f.i % 2 ? -1 : 1, sh.gold);
      c.restore();
    }
    c.strokeStyle = sh ? (sh.gold ? GOLD : GREEN) : RED;
    c.lineWidth = 5;
    c.beginPath();
    c.arc(x, y, L.cell * (0.25 + 0.25 * k), 0, Math.PI * 2);
    c.stroke();
    text(c, sh ? `+${f.points}` : "miss", x, y - L.cell * 0.2 - k * 40, sh ? 30 : 20, sh ? (sh.gold ? GOLD : GREEN) : RED);
    c.globalAlpha = 1;
  }

  const left = DURATION - t, m = mult(g), cx = W / 2;
  c.fillStyle = "rgba(255,255,255,.12)";
  c.fillRect(0, 0, W, 6);
  c.fillStyle = left < 5000 ? RED : GOLD;
  c.fillRect(0, 0, (W * left) / DURATION, 6);
  text(c, "SCORE", 20, 32, 12, "rgba(255,255,255,.6)", "left");
  text(c, g.score, 20, 76, 44, GOLD, "left");
  text(c, `${Math.ceil(left / 1000)}s`, W - 20, 62, 32, "#fff", "right");
  text(c, "MULTIPLIER", cx, 30, 12, "rgba(255,255,255,.6)");
  text(c, `x${m}`, cx, 66, 36, m > 1 ? GOLD : "#fff");
  for (let k = 0; k < STEP; k++) {
    c.fillStyle = m === MULT_MAX || k < g.streak % STEP ? GOLD : "rgba(255,255,255,.2)";
    c.beginPath();
    c.arc(cx + (k - (STEP - 1) / 2) * 14, 84, 4, 0, Math.PI * 2);
    c.fill();
  }
  if (r.mode === "count") {
    const k = (now - r.at) / 1000;
    c.globalAlpha = 1 - (k % 1) * 0.6;
    text(c, 3 - Math.floor(k), cx, H * 0.55, 120 - (k % 1) * 20, GOLD);
    c.globalAlpha = 1;
  }
  if (r.mode === "run" && t < 600) text(c, "GO!", cx, H * 0.55, 96, GREEN);
  if (r.mode === "done") text(c, "TIME", cx, H * 0.55, 72, "#fff");
}

const Card = ({ children }) => (
  <div className="pointer-events-none absolute inset-0 grid place-items-center p-4">
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -16 }}
      className="hud-panel hud-thick pointer-events-auto relative w-full max-w-sm rounded-[28px] p-6 text-center">
      {children}
    </motion.div>
  </div>
);

const Label = ({ children }) => <div className="text-sm font-medium text-x-mute">{children}</div>;
const Pill = ({ children, gold }) => <span className={`rounded-full px-3 py-1 text-xs font-semibold ${gold ? "bg-x-gold-soft text-x-gold-ink" : "bg-x-raised"}`}>{children}</span>;

export default function HarbourRush({ seed, title, stake = 0, target, onFinish, onClose, result }) {
  const cv = useRef(null);
  const R = useRef({ mode: "intro", g: newGame(seed), t: 0, at: 0, taps: [], fx: [] });
  const cb = useRef({});
  const [ui, setUi] = useState("intro");
  cb.current = { onFinish, onClose };

  const start = () => {
    Object.assign(R.current, { mode: "count", at: performance.now() });
    setUi("play");
  };

  useEffect(() => {
    const canvas = cv.current, c = canvas.getContext("2d");
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf, W, H;
    const fit = () => {
      const d = window.devicePixelRatio || 1;
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * d;
      canvas.height = H * d;
      c.setTransform(d, 0, 0, d, 0, 0);
    };
    const step = (now) => {
      const r = R.current;
      if (r.mode === "count" && now - r.at >= 3000) Object.assign(r, { mode: "run", at: r.at + 3000 });
      if (r.mode === "run") {
        r.t = Math.max(0, Math.min(DURATION, Math.floor(now - r.at)));
        advance(r.g, r.t);
        if (r.t === DURATION) Object.assign(r, { mode: "done", endAt: now });
      }
    };
    // Timed from the touch itself, but never before a moment the game has already played out.
    const tap = (e) => {
      step(performance.now());
      const r = R.current, L = grid(W, H);
      const col = Math.floor((e.clientX - L.x0) / L.cell), row = Math.floor((e.clientY - L.y0) / L.cell);
      if (r.mode !== "run" || r.taps.length >= MAX_TAPS || col < 0 || col >= COLS || row < 0 || row >= ROWS) return;
      const t = Math.max(r.g.now, Math.round(Math.min(e.timeStamp, performance.now()) - r.at)), s = row * COLS + col;
      if (t > DURATION) return;
      r.taps.push([t, s]);
      const d = tapAt(r.g, t, s);
      r.fx.push({ ...d, s, t0: performance.now() });
      if (d.result === "hit") sfx("coin");
    };
    // Caught before the city's shortcuts, which would close the panel this game lives in and lose the run.
    const key = (e) => {
      e.stopPropagation();
      if (e.key === "Escape" && R.current.mode === "intro") cb.current.onClose();
    };
    // performance.now(), not the frame's timestamp: taps are stamped with it, and a stalled frame can start before them.
    const frame = () => {
      const r = R.current, now = performance.now();
      step(now);
      if (r.mode === "done" && now - r.endAt >= SETTLE) {
        r.mode = "over";
        setUi("end");
        cb.current.onFinish(r.taps);
      }
      paint(c, W, H, now, r, calm);
      raf = requestAnimationFrame(frame);
    };
    fit();
    raf = requestAnimationFrame(frame);
    window.addEventListener("resize", fit);
    window.addEventListener("keydown", key, true);
    canvas.addEventListener("pointerdown", tap);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      window.removeEventListener("keydown", key, true);
      canvas.removeEventListener("pointerdown", tap);
    };
  }, []);

  const { g } = R.current;
  return (
    <div className="fixed inset-0 z-[80] select-none bg-ink">
      <canvas ref={cv} className="h-full w-full touch-none" />
      <AnimatePresence>
        {ui === "intro" && (
          <Card key="intro">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 text-x-mute hover:text-x-ink" data-testid="rush-close">
              <X className="h-5 w-5" strokeWidth={3} />
            </button>
            <Label>Harbour Rush</Label>
            <h2 className="mt-1 text-2xl font-semibold">{title}</h2>
            <p className="mt-3 text-sm text-x-mute">Tap the ships before they sail off, the sooner the more they're worth. Every {STEP} in a row raises your multiplier, up to x{MULT_MAX}. Gold ships are worth 3x. A miss resets the streak. 30 seconds.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Pill>{stake ? `${stake} UXD on the line` : "Free play"}</Pill>
              {target && <Pill gold>Beat {target.score}{target.h ? ` · @${target.h}` : ""}</Pill>}
            </div>
            <button type="button" autoFocus onClick={start} data-testid="rush-start"
              className="chip-btn mt-5 w-full py-3 text-lg">
              Start
            </button>
          </Card>
        )}
        {ui === "end" && (
          <Card key="end">
            <Label>Time's up</Label>
            <div className="mt-1 text-6xl font-semibold tabular-nums" data-testid="rush-score">{g.score}</div>
            <div className="mt-1 text-sm text-x-mute">{g.hits} ships · <span className="font-semibold text-x-pos">best streak {g.best}</span></div>
            <div className="mt-4 flex min-h-[2.5rem] items-center justify-center text-sm font-semibold" data-testid="rush-result">
              {result
                ? <span className={result.won ? "text-x-pos" : result.won === false ? "text-x-rose-ink" : "text-x-ink"}>{result.text}</span>
                : <span className="flex items-center gap-2 text-x-mute"><Loader2 className="h-4 w-4 animate-spin" />Sending your run...</span>}
            </div>
            <button type="button" onClick={onClose} data-testid="rush-done"
              className="chip-btn chip-soft mt-4 w-full py-3">
              Close
            </button>
          </Card>
        )}
      </AnimatePresence>
    </div>
  );
}
